/**
 * Unified Tracking Utility
 * - Reddit Pixel (a2_iml2frjy2yrd) — events prefixed cody_*
 * - Google Analytics (G-Y22J1RHDPS) — standard GA4 events + custom events
 */

function rdt_track(eventName, params = {}) {
  try {
    if (window.rdt) {
      window.rdt('track', 'Custom', { customEventName: `cody_${eventName}`, ...params });
    }
  } catch {}
}

function ga_track(eventName, params = {}) {
  try {
    if (window.gtag) {
      window.gtag('event', eventName, params);
    }
  } catch {}
}

function track(eventName, gaEvent, gaParams = {}, rdtParams = {}) {
  rdt_track(eventName, rdtParams);
  ga_track(gaEvent, gaParams);
}

export const analytics = {
  // ── Page Views ──
  pageVisit: (path) => {
    rdt_track('PageVisit');
    ga_track('page_view', { page_path: path, page_title: document.title });
  },

  // ── Auth Events ──
  signUp: (email, method = 'email') => {
    track('SignUp', 'sign_up', { method, user_email: email }, { content_name: email });
  },

  login: (email, method = 'email') => {
    track('Login', 'login', { method, user_email: email }, { content_name: email });
  },

  emailVerified: (email) => {
    track('EmailVerified', 'email_verified', { user_email: email });
  },

  forgotPassword: (email) => {
    ga_track('forgot_password', { user_email: email });
  },

  passwordReset: () => {
    ga_track('password_reset');
  },

  // ── Onboarding Events ──
  scanStarted: (url) => {
    track('ScanStarted', 'scan_started', { content_type: 'url', content_id: url }, { content_name: url });
  },

  scanCompleted: (productName, keywordsCount, subredditsCount) => {
    track('ScanCompleted', 'scan_completed', {
      product_name: productName,
      keywords_count: keywordsCount,
      subreddits_count: subredditsCount,
    }, { value: keywordsCount });
  },

  websiteAnalyzed: (url) => {
    track('WebsiteAnalyzed', 'website_analyzed', { content_id: url }, { content_name: url });
  },

  businessCreated: (name, url) => {
    track('BusinessCreated', 'business_created', { business_name: name, website_url: url }, { content_name: name });
  },

  businessDeleted: (name) => {
    track('BusinessDeleted', 'business_deleted', { business_name: name });
  },

  onboardReviewStarted: () => {
    ga_track('onboard_review_started');
  },

  onboardScanStarted: () => {
    track('OnboardScanStarted', 'onboard_scan_started');
  },

  // ── Lead Events ──
  leadViewed: (leadId, score, subreddit) => {
    track('LeadViewed', 'select_content', {
      content_type: 'lead',
      content_id: leadId,
      intent_score: score,
      subreddit,
    }, { content_ids: [leadId], value: score });
  },

  leadStatusChanged: (leadId, status) => {
    track('LeadStatusChanged', 'lead_status_changed', {
      content_id: leadId,
      status,
    }, { content_name: status });
  },

  leadsRefreshed: (newLeadsCount) => {
    track('LeadsRefreshed', 'leads_refreshed', { new_leads: newLeadsCount });
  },

  leadSearched: (query) => {
    ga_track('search', { search_term: query });
  },

  leadFiltered: (filterType, filterValue) => {
    ga_track('lead_filtered', { filter_type: filterType, filter_value: filterValue });
  },

  // ── AI Reply Events ──
  replyGenerated: (leadId, tone) => {
    track('ReplyGenerated', 'reply_generated', {
      content_id: leadId,
      tone,
    });
  },

  replyCopied: (leadId) => {
    track('ReplyCopied', 'reply_copied', { content_id: leadId });
  },

  replyRegenerated: (leadId) => {
    ga_track('reply_regenerated', { content_id: leadId });
  },

  // ── Keyword & Subreddit Events ──
  keywordAdded: (keyword) => {
    ga_track('keyword_added', { keyword });
  },

  keywordDeleted: (keyword) => {
    ga_track('keyword_deleted', { keyword });
  },

  keywordToggled: (keyword, enabled) => {
    ga_track('keyword_toggled', { keyword, enabled });
  },

  subredditAdded: (subreddit) => {
    ga_track('subreddit_added', { subreddit });
  },

  subredditDeleted: (subreddit) => {
    ga_track('subreddit_deleted', { subreddit });
  },

  // ── Upgrade / Payment Events ──
  upgradeClicked: (source, plan) => {
    track('UpgradeClicked', 'upgrade_clicked', {
      source,
      plan: plan || '',
    }, { content_name: source });
  },

  pricingViewed: () => {
    track('PricingViewed', 'pricing_viewed');
  },

  checkoutStarted: (packageId, amount, couponCode) => {
    track('CheckoutStarted', 'begin_checkout', {
      currency: 'USD',
      value: amount,
      items: [{ item_id: packageId, item_name: packageId, price: amount }],
      coupon: couponCode || undefined,
    });
  },

  purchaseCompleted: (plan, amount, sessionId) => {
    track('Purchase', 'purchase', {
      currency: 'USD',
      value: amount,
      transaction_id: sessionId,
      items: [{ item_id: plan, item_name: plan }],
    }, { content_name: plan });
  },

  couponApplied: (code, discountAmount) => {
    ga_track('coupon_applied', { coupon: code, discount: discountAmount });
  },

  couponFailed: (code, reason) => {
    ga_track('coupon_failed', { coupon: code, reason });
  },

  // ── Notification Events ──
  slackConnected: () => {
    ga_track('slack_connected');
  },

  emailNotifEnabled: () => {
    ga_track('email_notif_enabled');
  },

  settingsSaved: (section) => {
    ga_track('settings_saved', { section });
  },

  // ── Navigation Events ──
  navClicked: (item) => {
    ga_track('nav_clicked', { nav_item: item });
  },

  externalLinkClicked: (url) => {
    ga_track('external_link_click', { link_url: url });
  },

  // ── Engagement Events ──
  announcementDismissed: (text) => {
    ga_track('announcement_dismissed', { announcement: text });
  },

  ctaClicked: (ctaName, location) => {
    ga_track('cta_clicked', { cta_name: ctaName, location });
  },

  // ── Generic ──
  custom: (name, params = {}) => {
    rdt_track(name, params);
    ga_track(name, params);
  },
};

// Legacy export for backward compatibility
export const redditPixel = analytics;
export default analytics;
